import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'tdesign-react';
import { LockOnIcon } from 'tdesign-icons-react';
import ListPanel from '@/components/ListPanel';
import useTable from '@/hooks/useTable';
import { deleteRole, getRoleList } from '@/services/roleService';
import PermWrapper from '@/components/PermWrapper';
import { renderDateTime } from '@/utils/common';
import FormModal from './FormModal';
import PermissionModal from './PermissionModal';

const { FormItem } = Form;

export default function Role() {
  const [visible, setVisible] = useState(false);
  const [permissionVisible, setPermissionVisible] = useState(false);
  const [detail, setDetail] = useState(null);

  const columns = [
    {
      title: '角色名称',
      colKey: 'name',
      cell: ({ row }) => (
        <Space size={4}>
          {row.name === '超级管理员' ? <Tag theme='danger'>超级管理员</Tag> : <Tag>{row.name}</Tag>}
          {!row.enabled && (
            <Tooltip content='系统内置'>
              <LockOnIcon />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '角色说明',
      colKey: 'desc',
    },
    {
      title: '创建时间',
      colKey: 'addTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          {row.enabled && (
            <PermWrapper resource='pulsar:sys:role:update'>
              <Link
                theme='primary'
                onClick={() => {
                  setDetail(row);
                  setVisible(true);
                }}
                hover='color'
              >
                编辑
              </Link>
            </PermWrapper>
          )}

          <PermWrapper resource='pulsar:sys:role:empower'>
            <Link
              theme='primary'
              onClick={() => {
                setDetail(row);
                setPermissionVisible(true);
              }}
              hover='color'
            >
              授权
            </Link>
          </PermWrapper>
          {row.enabled && (
            <PermWrapper resource='pulsar:sys:role:delete'>
              <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
                <Link theme='danger' hover='color'>
                  删除
                </Link>
              </Popconfirm>
            </PermWrapper>
          )}
        </Space>
      ),
      width: 180,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { data } = await getRoleList({ ...params, page: 1, limit: 99999 });
    setData(data);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <FormItem name='name'>
        <Input clearable placeholder='请输入角色名称' />
      </FormItem>
    ),
    operators: (
      <PermWrapper resource='pulsar:sys:role:create'>
        <Button onClick={() => setVisible(true)}>新增角色</Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const doDelete = async (row) => {
    await deleteRole({ id: row.id });
    MessagePlugin.success('操作成功');
    doRefresh();
  };

  const handleClose = (refresh) => {
    setVisible(false);
    setPermissionVisible(false);
    setDetail(null);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <ListPanel header='角色管理'>
      {header}
      <Table {...config} />
      <FormModal visible={visible} detail={detail} handleClose={handleClose} />
      <PermissionModal detail={detail} visible={permissionVisible} handleClose={handleClose} />
    </ListPanel>
  );
}
